/**
 * m3 echo Frontend - Login Page
 *
 * Modern, elegant login form with email and password authentication.
 * Uses Material-UI components for styling.
 */

import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Fade,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { authService } from '../services/auth';
import { useAuthStore } from '../stores/useAuthStore';
import { useReducedMotion } from '../hooks/useReducedMotion';

// Microsoft Entra ID SSO is temporarily disabled. Flip to true and also set
// SSO_ENABLED=True in backend/app/api/sso.py to bring the feature back.
const SSO_ENABLED = false;

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoading } = useAuthStore();
  const reduceMotion = useReducedMotion();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    setLoading(true);

    try {
      const response = await authService.login({ email, password });
      setAuth(response);

      // During maintenance, non-admin users always go to / (ProtectedRoute shows maintenance page)
      if (response.maintenance_mode && !response.user.is_admin) {
        navigate('/');
      } else if (response.user.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 33%, #dfe9f3 66%, #e8ecf1 100%)',
        backgroundSize: '400% 400%',
        ...(!reduceMotion && {
          animation: 'gradientDrift 15s ease infinite',
          '@keyframes gradientDrift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }),
        py: 6,
        px: 2,
      }}
    >
      <Fade in timeout={reduceMotion ? 0 : 600}>
      <Box sx={{ maxWidth: 450, width: '100%' }}>
        {/* Login Card */}
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'visible',
            ...(!reduceMotion && {
              animation: 'cardEntrance 600ms cubic-bezier(0.0,0,0.2,1)',
              '@keyframes cardEntrance': {
                '0%': { opacity: 0, transform: 'translateY(16px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }),
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                m3 <Box component="span" sx={{ color: '#678BA3' }}>echo</Box>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.95rem' }}
              >
                Geben Sie Ihre Anmeldedaten ein, um fortzufahren
              </Typography>
            </Box>

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* Email Field */}
              <TextField
                fullWidth
                label="E-Mail / Benutzername"
                type="text"
                variant="outlined"
                placeholder="ihre-email@beispiel.de oder admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
                autoComplete="email"
                sx={{ mb: 2.5 }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                label="Passwort"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                autoComplete="current-password"
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot Password */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Link
                  href="#"
                  variant="body2"
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Passwort vergessen?
                </Link>
              </Box>

              {/* Submit Button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Anmeldung läuft...
                  </Box>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </Box>

            {/* Microsoft SSO (Entra ID) is temporarily disabled. Flip SSO_ENABLED in
                backend/app/api/sso.py and re-render this block to bring it back. */}
            {SSO_ENABLED && (
              <>
                <Divider sx={{ my: 3 }}>oder</Divider>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    const domain = email.includes('@') ? email.split('@')[1] : '';
                    window.location.href = `/api/auth/sso/login?tenant_hint=${encodeURIComponent(domain)}`;
                  }}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    backgroundColor: '#0078d4',
                    '&:hover': {
                      backgroundColor: '#106ebe',
                    },
                    boxShadow: 2,
                  }}
                >
                  Mit Microsoft anmelden
                </Button>
              </>
            )}

            {/* Create Account Link */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Sie haben noch kein Konto?{' '}
                <Link
                  href="#"
                  sx={{
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Erstellen Sie eines
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/impressum" color="inherit" underline="hover">
            Impressum
          </Link>
        </Typography>

      </Box>
      </Fade>
    </Box>
  );
};
